import type { IUserData } from "../shared/session/interfaces/session";
import { PayoutMethodType, type PayoutInput } from "../interfaces/payout";
import type { ComposeSelection } from "../store/useComposeStore";
import { resolvePayoutMethod } from "./catalog";

const toMethodType = (
  canonical: string | undefined,
): PayoutMethodType | null => {
  switch (canonical) {
    case "BANK_ACCOUNT":
      return PayoutMethodType.BANK_ACCOUNT;
    case "CASH_PICKUP":
      return PayoutMethodType.CASH_PICKUP;
    case "PUSH_TO_CARD":
      return PayoutMethodType.PUSH_TO_CARD;
    case "MOBILE_WALLET":
      return PayoutMethodType.MOBILE_WALLET;
    default:
      return null;
  }
};

/** Maps claim `userData.paymentMethod` into compose selection (default row). */
export function claimDefaultPayoutSelection(
  userData: IUserData | undefined | null,
): ComposeSelection | null {
  const pm = userData?.paymentMethod;
  if (!pm) return null;
  const canonical = resolvePayoutMethod(userData) ?? pm.payoutMethod;
  const method = toMethodType(canonical);
  if (!method) return null;

  const payout: PayoutInput = { source: "new" };

  if (method === PayoutMethodType.CASH_PICKUP) {
    const payer = pm.cash?.payer ?? pm.cash?.provider;
    if (payer) payout.payerId = payer.toUpperCase();
    const cashState = (pm.cash as { state?: string } | undefined)?.state;
    if (cashState) payout.stateCode = cashState;
  } else if (method === PayoutMethodType.BANK_ACCOUNT) {
    const bank = pm.bankAccount;
    if (bank?.bankCode) payout.bankId = bank.bankCode.toUpperCase();
    // The claim only ever carries a masked account (`****123456`). Keep the
    // visible digits for the label; the real account lives in the link and is
    // resolved server-side at create. Copying the mask into `accountNumber`
    // would send `*` to register and fail DTO validation.
    const digits = bank?.accountNumber?.replace(/\D/g, "");
    if (digits) payout.last4 = digits.slice(-4);
  } else if (method === PayoutMethodType.PUSH_TO_CARD) {
    const card = pm.card;
    if (card?.last4) payout.last4 = card.last4;
    if (card?.brand) payout.brand = card.brand;
  } else if (method === PayoutMethodType.MOBILE_WALLET) {
    const w = pm.mobileWallet;
    if (w?.carrier) payout.carrierId = w.carrier.toUpperCase();
    if (w?.phone) payout.phone = w.phone;
  }

  return { method, option: null, payout, origin: "claim" };
}

export function resolveComposePayerCode(
  selection: ComposeSelection | null | undefined,
): string | undefined {
  if (!selection) return undefined;
  const { method, payout } = selection;
  if (method === PayoutMethodType.CASH_PICKUP) return payout.payerId;
  if (method === PayoutMethodType.MOBILE_WALLET) return payout.carrierId;
  if (method === PayoutMethodType.BANK_ACCOUNT) return payout.bankId;
  return undefined;
}

/**
 * Payer code safe to pin on a pre-quote. A saved bank row from the recipient
 * book carries the bank's display name in `bankId`, which matches no catalog
 * payer — pinning it would price zero SKUs and read as an unavailable route.
 */
export function pinnablePayerCode(
  selection: ComposeSelection | null | undefined,
): string | undefined {
  if (!selection) return undefined;
  if (
    selection.method === PayoutMethodType.BANK_ACCOUNT &&
    selection.payout.source === "saved"
  ) {
    return undefined;
  }
  return resolveComposePayerCode(selection);
}
